import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { Seo } from 'src/components/seo';
import { queryAggregatorStatus } from 'src/api/query-logic/query-aggregator-status';
import LoadingState from 'src/e2e-redesign/components/LoadingState';
import SingleLogo from 'src/e2e-redesign/components/SingleLogo';
import ThemedCard from 'src/e2e-redesign/components/ThemedCard';
import { parseHtml } from 'src/utils/general';
import NewDumbLenderResponseTable from 'src/e2e-redesign/views/LenderResponses/new-dumb-lender-response-table';
import AggregatorError from './aggregator-error';
import { transformAggregatorStatusResponse } from 'src/utils/aggregator-transformers';
import {
  getAggregatorStatusDisplay,
  getAggregatorStatusMessage,
  shouldShowAggregatorOffers,
} from 'src/utils/aggregator-status-helpers';
import { getAggregatorErrorMessage } from 'src/utils/aggregator-error-helpers';
import { createAggregatorPollingConfig } from 'src/utils/aggregator-polling';
import { AGGREGATOR_ERROR_MESSAGES, AGGREGATOR_STATUS } from 'src/utils/aggregator.constants';

export default function AggregatorResponse() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [pollingStartTime] = useState(Date.now());
  const theme = useTheme();

  const { data: statusData, isLoading, isError, error } = queryAggregatorStatus({
    reqData: {
      query: token ? { token } : undefined,
    },
    queryKey: [token || ''],
    queryOpt: {
      enabled: !!token,
      refetchInterval: createAggregatorPollingConfig(pollingStartTime),
    },
  });

  // Transform aggregator response to match normal application structure
  // MUST be called before any early returns to follow Rules of Hooks
  const transformedData = useMemo(() => {
    return transformAggregatorStatusResponse(statusData);
  }, [statusData]);

  // Early returns AFTER all hooks
  if (!token) {
    return <AggregatorError message={AGGREGATOR_ERROR_MESSAGES.MISSING_TOKEN} errorCode={400} />;
  }

  if (isLoading && !statusData) {
    return <LoadingState title="Loading Status" paragraph="Please wait while we load your application status." />;
  }

  if (isError) {
    const errorMessage = getAggregatorErrorMessage(error, 'status');
    return <AggregatorError message={errorMessage} errorCode={(error as any)?.statusCode} />;
  }

  if (!statusData) {
    return <AggregatorError message={AGGREGATOR_ERROR_MESSAGES.UNABLE_TO_LOAD} errorCode={404} />;
  }

  const statusMessage = getAggregatorStatusMessage(statusData);
  const shouldShowOffers = shouldShowAggregatorOffers(statusData);
  const hasOffers = statusData.lender_offers && statusData.lender_offers.length > 0;

  return (
    <>
      <Seo title="Application Status" />
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        {statusData.lender_logo && (
          <Box mb={2}>
            <SingleLogo
              image={{
                src: statusData.lender_logo,
                alt: statusData.lender_name || 'Lender Logo',
              }}
            />
          </Box>
        )}
        {statusData.lender_name && (
          <Typography variant="h4" sx={{ marginBottom: '20px', fontWeight: 700, color: '#243F8F' }}>
            {statusData.lender_name}
          </Typography>
        )}

        <ThemedCard sx={{ marginBottom: '20px', overflowX: 'auto' }}>
          <Typography variant="h5" sx={{ marginBottom: '20px', fontWeight: 600 }}>
            Application Status: {getAggregatorStatusDisplay(statusData)}
          </Typography>

          {statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PENDING && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Your application is being processed. This page will update automatically.
              </Typography>
            </Box>
          )}

          {shouldShowOffers && transformedData && (
            <Box sx={{ marginTop: '30px' }}>
              <NewDumbLenderResponseTable
                submissionDetails={transformedData.submissionDetails}
                index={0}
                offerLender={transformedData.lender}
                lenderDisclosures={undefined}
                offerDetails={transformedData.offer.offer_details}
                offer={transformedData.offer}
                isPending={statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PENDING}
                selectedOffer=""
                handleOfferSelection={() => { }}
                showEcoa={false}
                redirectUrl={undefined}
                showUpdateStatus={false}
                handleUpdateStatus={async () => { }}
                handleAcceptAndProceed={() => { }}
                disableActions={false}
              />
            </Box>
          )}

          {statusData.approved_amount && !hasOffers && (
            <Box sx={{ marginTop: '30px' }}>
              <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 600 }}>
                Approved Amount
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }}>
                ${statusData.approved_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}

          {!shouldShowOffers &&
            (statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.APPROVED ||
              statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PRE_APPROVED) &&
            !statusData.approved_amount && (
              <Typography variant="body2" sx={{ marginTop: '20px', color: '#666' }}>
                No offer details available at this time.
              </Typography>
            )}
        </ThemedCard>
      </Box>
    </>
  );
}
